import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFetch } from "../hooks/useFetch";
import API from "../api/axios";
import { useSelector } from "react-redux";
import { useShowToast } from "../store/hooks";
import { formatDate, playAlarmSound, localTomorrowStr, mapsNavigateUrl, formatTimeRange12, dayTagLabel, getLocalDateStr, isCancelLocked, isUnpaidCancelled } from "../utils/constants";
import { useLocalDay } from "../hooks/useLocalDay";
import CookAvailabilityToggle from "../components/CookAvailabilityToggle";
import CookEventBookings from "../components/CookEventBookings";
import { resolveFileUrl } from "../components/CookDocUploads";
import BookingRequestModal from "../components/BookingRequestModal";
import CookCancelModal from "../components/CookCancelModal";
import { Check, XCircle, BellRing, ArrowRight, Star, MapPin, Navigation, CalendarDays, CalendarCheck, CalendarClock, Inbox, History, UserRound, Users, Soup, Wallet, ChefHat, AlertCircle, ShieldCheck } from "lucide-react";

const customerLocationLabel = (booking) => {
  const d = booking?.addressDetails || {};
  const primary =
    [d.flatNo, d.society].filter(Boolean).join(", ") || booking?.address || "";
  return [primary, d.landmark ? `Near ${d.landmark}` : "", d.city]
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .join(" · ");
};

const CookDashboard = () => {
  const { data: bookings, loading: loadingBookings, refreshing: refreshingBookings, error: bookingError, refetch: refetchBookings } = useFetch("/bookings/cook");
  const { data: broadcastRequests, refetch: refetchBroadcast } = useFetch("/bookings/cook/requests");
  const { data: scheduledToday, refetch: refetchToday } = useFetch("/bookings/cook/schedule?day=today");
  const { data: scheduledTomorrow, refetch: refetchTomorrow } = useFetch("/bookings/cook/schedule?day=tomorrow");
  const { data: cookProfile, loading: loadingProfile, refetch: refetchCookProfile } = useFetch("/cooks/me");
  const { data: myReviews } = useFetch("/reviews/cook-me");
  const { data: myComplaints } = useFetch("/complaints/my");
  const showToast = useShowToast();
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const today = useLocalDay();
  const [view, setView] = useState("today");
  const [pendingCancelId, setPendingCancelId] = useState(null);
  const [actingOn, setActingOn] = useState(null);
  const [requestModalBooking, setRequestModalBooking] = useState(null);
  const [photoOk, setPhotoOk] = useState(true);
  useEffect(() => setPhotoOk(true), [cookProfile?.photoUrl]);
  const seenHoursDone = useRef(new Set());
  const firstLoadDone = useRef(false);
  const seenRequestIds = useRef(new Set());
  const requestsInit = useRef(false);
  const handledRequestIds = useRef(new Set());

  useEffect(() => {
    try {
      if ("Notification" in window && Notification.permission === "default") {
        const p = Notification.requestPermission();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    } catch {
    }
  }, []);

  useEffect(() => {
    const tick = () => {
      if (!document.hidden) {
        refetchBookings();
        refetchBroadcast();
        refetchToday();
        refetchTomorrow();
      }
    };
    const id = setInterval(tick, 15000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetchBookings, refetchBroadcast, refetchToday, refetchTomorrow]);
  const refetchAllRequests = () => {
    refetchBookings();
    refetchBroadcast();
    refetchToday();
    refetchTomorrow();
  };

  const modalIdRef = useRef(null);
  modalIdRef.current = requestModalBooking?._id || null;
  useEffect(() => {
    const myId = String(user?._id || user?.id || "");
    const onRequest = () => {
      refetchBookings();
      refetchBroadcast();
    };
    const closeIfOpen = (id, message) => {
      if (!id) {
        refetchBookings();
        refetchBroadcast();
        return;
      }
      if (modalIdRef.current && String(modalIdRef.current) === String(id)) {
        handledRequestIds.current.add(String(id));
        setRequestModalBooking(null);
        if (message) showToast(message, "info");
      }
      refetchBookings();
      refetchBroadcast();
      refetchToday();
      refetchTomorrow();
    };
    const onAssigned = (e) => {
      const d = e?.detail || {};
      const id = d.bookingId ? String(d.bookingId) : null;
      const wonByMe = myId && d.assignedCookId && String(d.assignedCookId) === myId;
      closeIfOpen(
        id,
        id && !wonByMe ? "This request was just accepted — it is no longer available." : null
      );
    };
    const onExpired = (e) => {
      const d = e?.detail || {};
      const id = d.bookingId ? String(d.bookingId) : null;
      closeIfOpen(id, id ? "This request expired — the slot was released." : null);
    };
    const onIgnored = (e) => {
      const d = e?.detail || {};
      if (myId && d.cookId && String(d.cookId) === myId) {
        refetchBroadcast();
      }
    };
    window.addEventListener("realtime-booking-request", onRequest);
    window.addEventListener("realtime-booking-assigned", onAssigned);
    window.addEventListener("realtime-booking-expired", onExpired);
    window.addEventListener("realtime-booking-ignored", onIgnored);
    return () => {
      window.removeEventListener("realtime-booking-request", onRequest);
      window.removeEventListener("realtime-booking-assigned", onAssigned);
      window.removeEventListener("realtime-booking-expired", onExpired);
      window.removeEventListener("realtime-booking-ignored", onIgnored);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.id]);

  useEffect(() => {
    if (!bookings?.length) return;
    if (!firstLoadDone.current) {
      bookings.forEach((b) => {
        if (b.hoursCompleted) seenHoursDone.current.add(b._id);
      });
      firstLoadDone.current = true;
      return;
    }
    bookings.forEach((b) => {
      if (b.hoursCompleted && !seenHoursDone.current.has(b._id)) {
        seenHoursDone.current.add(b._id);
        showToast(
          `Cooking hours complete for ${b.customer?.name || "your booking"} — please wrap up.`,
          "warning",
          8000
        );
        playAlarmSound();
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Cooking hours complete!", {
              body: "Your booked cooking hours are complete. Please wrap up the session.",
            });
          }
        } catch {
        }
      }
    });
  }, [bookings, showToast]);

  const openBooking = (e, bookingId) => {
    if (e.target.closest("button, a, input, select, textarea")) return;
    navigate(`/bookings/${bookingId}`);
  };
  const openBookingKey = (e, bookingId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/bookings/${bookingId}`);
    }
  };

  const handleCancel = () => {
    setPendingCancelId(null);
    refetchBookings();
  };

  const handleRequestAction = async (booking, action) => {
    if (actingOn || !booking?._id) return;
    setActingOn(booking._id);
    try {
      const res = await API.patch(`/bookings/${booking._id}/${action}`);
      if (action === "accept") {
        showToast("Booking accepted — the customer has 5 minutes to pay.", "success");
      } else if (res.data?.ignored) {
        showToast("Request ignored — the customer is still waiting for another cook.", "info");
      } else {
        showToast("Booking request ignored.", "info");
      }
      handledRequestIds.current.add(String(booking._id));
      if (String(requestModalBooking?._id) === String(booking._id)) {
        setRequestModalBooking(null);
      }
      refetchAllRequests();
      popNextPendingRequest(booking._id);
    } catch (err) {
      const gone = err.response?.status === 410 || err.response?.status === 409;
      showToast(
        err.response?.data?.code === "BOOKING_ALREADY_ASSIGNED"
          ? "This request has already been accepted by another cook."
          : err.response?.data?.message || `Failed to ${action} booking`,
        "error"
      );
      if (gone) {
        handledRequestIds.current.add(String(booking._id));
        if (String(requestModalBooking?._id) === String(booking._id)) {
          setRequestModalBooking(null);
        }
        refetchAllRequests();
        popNextPendingRequest(booking._id);
      }
    } finally {
      setActingOn(null);
    }
  };

  const isPaidBooking = (b) => b?.payment?.status === "paid";
  const getStatusLabel = (booking) => {
    const status = typeof booking === "string" ? booking : booking?.status;
    switch (status) {
      case "requested":
        return <span className="badge badge-amber">Action Needed</span>;
      case "accepted":
        if (typeof booking !== "string" && !isPaidBooking(booking)) {
          return <span className="badge badge-amber">Awaiting Payment</span>;
        }
        return <span className="badge badge-blue">Scheduled</span>;
      case "confirmed":
        return <span className="badge badge-blue">Scheduled</span>;
      case "in_progress":
        return <span className="badge badge-purple">In Progress</span>;
      case "completed":
        return <span className="badge badge-emerald">Completed</span>;
      case "unattended":
        return <span className="badge badge-rose">Unattended</span>;
      case "rejected":
        return <span className="badge badge-rose">Declined</span>;
      case "expired":
        return <span className="badge badge-slate">Expired — No Response</span>;
      case "cancelled":
        return <span className="badge badge-slate">Cancelled</span>;
      default:
        return <span className="badge badge-slate">{status}</span>;
    }
  };

  const directPending = bookings?.filter((b) => b.status === "requested")?.length || 0;
  const broadcastPending = (broadcastRequests || []).filter((b) => b.status === "requested")?.length || 0;
  const pendingRequests = directPending + broadcastPending;

  const isPrevious = (b) => ["completed", "cancelled", "rejected"].includes(b.status);
  const tomorrowStr = (() => {
    const m = String(today || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return localTomorrowStr();
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setDate(d.getDate() + 1);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();
  const isTodayBooking = (b) => getLocalDateStr(b?.date) === today;
  const isTomorrowBooking = (b) => getLocalDateStr(b?.date) === tomorrowStr;
  const isNotCancelled = (b) => b?.status !== 'cancelled';
  const createdMs = (b) => {
    const t = new Date(b?.createdAt).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const byNewest = (a, b) =>
    createdMs(b) - createdMs(a) ||
    String(b._id || "").localeCompare(String(a._id || ""));
  const SCHEDULED_STATUSES = ["accepted", "confirmed", "in_progress", "completed"];
  const isScheduledBooking = (b) =>
    isPaidBooking(b) && SCHEDULED_STATUSES.includes(b?.status);
  const scheduledTodayList = [...(scheduledToday || [])].filter(isScheduledBooking);
  const scheduledTomorrowList = [...(scheduledTomorrow || [])].filter(isScheduledBooking);
  const visibleBookings = (view === 'needs-action'
    ? [...(bookings || []), ...(broadcastRequests || [])]
    : view === 'today'
      ? scheduledTodayList
      : view === 'tomorrow'
        ? scheduledTomorrowList
        : [...(bookings || [])]
  )
    .filter((b) => !isUnpaidCancelled(b))
    .filter((b) => {
      if (view === 'today') return isTodayBooking(b) && isNotCancelled(b) && isScheduledBooking(b);
      if (view === 'tomorrow') return isTomorrowBooking(b) && isNotCancelled(b) && isScheduledBooking(b);
      if (view === 'needs-action') return b.status === 'requested';
      if (view === 'upcoming') return !isPrevious(b);
      if (view === 'previous') return isPrevious(b);
      return true;
    })
    .sort((a, b) =>
      view === 'today' || view === 'tomorrow'
        ? String(a.startTime || "").localeCompare(String(b.startTime || "")) || byNewest(a, b)
        : byNewest(a, b)
    );
  const previousCount = (bookings || []).filter((b) => isPrevious(b) && !isUnpaidCancelled(b)).length;
  const todayCount = scheduledTodayList.length;
  const tomorrowCount = scheduledTomorrowList.length;
  const upcomingCount = (bookings || []).filter((b) => !isPrevious(b) && !isUnpaidCancelled(b)).length;
  const ratingCount = cookProfile?.rating?.count ?? myReviews?.length ?? 0;
  const avgRating =
    cookProfile?.rating?.average ||
    (myReviews?.length
      ? (myReviews.reduce((s, r) => s + Number(r.rating || 0), 0) / myReviews.length).toFixed(1)
      : null);
  const firstName = user?.name?.split(" ")[0] || "Chef";
  const approval = cookProfile?.approvalStatus;

  const alertNewRequest = () => {
    playAlarmSound();
  };

  useEffect(() => {
    if (!bookings && !broadcastRequests) return;
    const requested = [...(bookings || []), ...(broadcastRequests || [])]
      .filter((b) => b?.status === "requested" && !handledRequestIds.current.has(String(b?._id)))
      .sort(byNewest);
    if (!requestsInit.current) {
      requestsInit.current = true;
      if (!requested.length) return;
      requested.slice(1).forEach((b) => seenRequestIds.current.add(String(b._id)));
      const newest = requested[0];
      seenRequestIds.current.add(String(newest._id));
      if (!requestModalBooking) {
        setRequestModalBooking(newest);
        alertNewRequest();
      }
      return;
    }
    const fresh = requested.filter((b) => !seenRequestIds.current.has(String(b._id)));
    if (!fresh.length) return;
    fresh.forEach((b) => seenRequestIds.current.add(String(b._id)));
    if (!requestModalBooking) {
      const newest = [...fresh].sort(byNewest)[0];
      setRequestModalBooking(newest);
      alertNewRequest();
    } else {
      playAlarmSound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, broadcastRequests]);

  const popNextPendingRequest = (excludeId) => {
    const next = [...(bookings || []), ...(broadcastRequests || [])]
      .filter(
        (b) =>
          b?.status === "requested" &&
          String(b._id) !== String(excludeId || "") &&
          !handledRequestIds.current.has(String(b._id))
      )
      .sort(byNewest)[0];
    if (next) {
      seenRequestIds.current.add(String(next._id));
      setTimeout(() => {
        setRequestModalBooking(next);
        alertNewRequest();
      }, 350);
    }
  };

  return (
    <div className="dashboard-container cook-dash">
      <div className="cook-modern-hero cook-hero">
        <div className="cook-modern-hero-inner">
          <Link
            to="/dashboard/cook-profile"
            className="cook-modern-avatar-wrap cook-modern-avatar-link cook-hero-avatar-hide-mobile"
            title="Go to your profile"
            aria-label="Go to your profile"
          >
            {cookProfile?.photoUrl && photoOk ? (
              <img
                src={resolveFileUrl(cookProfile.photoUrl)}
                alt={user?.name || "Cook"}
                className="cook-modern-avatar"
                onError={() => setPhotoOk(false)}
              />
            ) : (
              <span className="cook-modern-avatar-fallback">
                {firstName?.[0]?.toUpperCase() || <ChefHat size={32} />}
              </span>
            )}
            {approval === "approved" && (
              <span className="verified-dot" title="Verified cook">✓</span>
            )}
          </Link>
          <div className="cook-modern-hero-copy">
            <h1 className="cook-hero-title">Hello, {firstName}</h1>
            <p className="cook-hero-sub">
              {pendingRequests > 0
                ? `${pendingRequests} new request${pendingRequests === 1 ? "" : "s"} waiting for you`
                : "You're all caught up — relax!"}
            </p>
            <div className="cook-hero-badges cook-hero-badges-hide-mobile">
              {approval === "approved" ? (
                <span className="hero-pill ok">Verified cook</span>
              ) : approval === "rejected" ? (
                <span className="hero-pill bad">Needs attention</span>
              ) : (
                <span className="hero-pill warn">{cookProfile ? "Under review" : "No profile yet"}</span>
              )}
              {avgRating ? (
                <span className="hero-pill"><Star size={12} /> {avgRating} ({ratingCount})</span>
              ) : (
                <span className="hero-pill">New chef</span>
              )}
              {cookProfile?.serviceArea && (
                <span className="hero-pill"><MapPin size={12} /> {cookProfile.serviceArea}</span>
              )}
            </div>
          </div>
          <div className="cook-modern-hero-actions">
            <CookAvailabilityToggle
              availabilityStatus={cookProfile?.availabilityStatus}
              onChanged={() => refetchCookProfile()}
            />
            <div className="cook-hero-btn-row">
              <Link className="cook-glass-btn" to="/dashboard/cook-profile">
                <UserRound size={15} /> Profile
              </Link>
              <Link className="cook-glass-btn" to="/dashboard/cook-reviews">
                <Star size={15} /> Reviews
              </Link>
              <Link className="cook-glass-btn" to="/cook/earnings">
                <Wallet size={15} /> Earnings
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="cook-stats-grid">
        <button type="button" className="cook-stat-card cook-stat-mob-hide" onClick={() => setView("needs-action")}>
          <div className="cook-stat-icon amber">
            <Inbox size={24} />
          </div>
          <div>
            <div className="cook-stat-num">{pendingRequests}</div>
            <div className="cook-stat-label">New requests</div>
          </div>
        </button>
        <button type="button" className="cook-stat-card cook-stat-mob-hide" onClick={() => setView("upcoming")}>
          <div className="cook-stat-icon blue">
            <CalendarDays size={24} />
          </div>
          <div>
            <div className="cook-stat-num">{upcomingCount}</div>
            <div className="cook-stat-label">Upcoming</div>
          </div>
        </button>
        <Link className="cook-stat-card cook-stat-mob-hide" to="/dashboard/cook-reviews">
          <div className="cook-stat-icon brand">
            <Star size={24} />
          </div>
          <div>
            <div className="cook-stat-num">{avgRating || "—"}</div>
            <div className="cook-stat-label">{ratingCount ? `${ratingCount} review${ratingCount === 1 ? "" : "s"}` : "No reviews yet"}</div>
          </div>
        </Link>
      </div>


      {!loadingProfile && (!cookProfile || approval !== "approved") && (
        <div className="cook-notice cook-notice-amber">
          <AlertCircle size={16} />
          <span>
            {!cookProfile
              ? "Create your profile to start receiving bookings."
              : approval === "rejected"
              ? "Your application needs attention — please update your profile."
              : "Your profile is under review — you'll be bookable once approved."}
          </span>
          <button onClick={() => navigate("/dashboard/cook-profile")} className="cook-link-btn">
            {cookProfile ? "Review profile →" : "Create profile →"}
          </button>
        </div>
      )}

      <div className="cook-tabs">
        <button
          className={`cook-tab ${view === "today" ? "active" : ""}`}
          onClick={() => setView("today")}
        >
          <CalendarCheck size={15} /> Today <span className="cook-tab-count">{todayCount}</span>
        </button>
        <button
          className={`cook-tab ${view === "tomorrow" ? "active" : ""}`}
          onClick={() => setView("tomorrow")}
        >
          <CalendarClock size={15} /> Tomorrow <span className="cook-tab-count">{tomorrowCount}</span>
        </button>
        <button
          className={`cook-tab ${view === "upcoming" ? "active" : ""}`}
          onClick={() => setView("upcoming")}
        >
          <CalendarDays size={15} /> Upcoming <span className="cook-tab-count">{upcomingCount}</span>
        </button>
        <button
          className={`cook-tab ${view === "previous" ? "active" : ""}`}
          onClick={() => setView("previous")}
        >
          <History size={15} /> Past <span className="cook-tab-count">{previousCount}</span>
        </button>
        <button
          className={`cook-tab ${view === "events" ? "active" : ""}`}
          onClick={() => setView("events")}
        >
          <CalendarDays size={15} /> Events
        </button>
        <button
          className={`cook-tab ${view === "reports" ? "active" : ""}`}
          onClick={() => setView("reports")}
        >
          <AlertCircle size={15} /> My reports{" "}
          <span className="cook-tab-count">{(myComplaints || []).length}</span>
        </button>
        <Link className="cook-tab" to="/cook/earnings">
          <Wallet size={15} /> Earnings
        </Link>
      </div>

      {/* BOOKINGS VIEWS */}
      {view !== "reports" && view !== "events" && (
        <div>

          {loadingBookings && <p className="cook-loading-text">Loading bookings...</p>}

          {!loadingBookings && refreshingBookings && (
            <div className="cook-syncing-pill" aria-live="polite" aria-label="Syncing bookings">
              <span className="cook-syncing-dot" />
              Syncing…
            </div>
          )}

          {bookingError && <p className="error">{bookingError}</p>}

          {bookings && bookings.length > 0 ? (
            visibleBookings.length > 0 ? (
            <div className="bookings-list-modern cook-bookings-grid">
              {visibleBookings.map((booking) => (
                <div
                  key={booking._id}
                  className={`booking-item-card cook-booking-card cb-card st-${booking.status} clickable`}
                  data-status={booking.status}
                  onClick={(e) => openBooking(e, booking._id)}
                  onKeyDown={(e) => openBookingKey(e, booking._id)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open booking details for ${booking.customer?.name || "booking"}`}
                >
                  <div className="cb-top">
                    <div className="cb-id">
                      <span className="cook-avatar cb-avatar" aria-hidden="true">
                        {booking.customer?.name?.[0]?.toUpperCase() || "C"}
                      </span>
                      <div className="cb-id-text">
                        <div className="cb-name-row">
                          <h3>{booking.customer?.name || "Client"}</h3>
                          {(() => {
                            const tag = dayTagLabel(booking, today);
                            return tag ? (
                              <span
                                className={`cook-day-tag ${tag === "Today" ? "is-today" : "is-tomorrow"}`}
                              >
                                {tag}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <p className="cb-sub">
                          {booking.serviceType ? (
                            <span className="cb-sub-item">
                              <Soup size={12} />
                              {String(booking.serviceType).replace(/_/g, " ")}
                            </span>
                          ) : null}
                          {booking.guests ? (
                            <span className="cb-sub-item">
                              <Users size={12} />
                              {booking.guests} guest{Number(booking.guests) === 1 ? "" : "s"}
                            </span>
                          ) : null}
                          {!booking.serviceType && !booking.guests ? (
                            <span className="cb-sub-item">Booking #{String(booking._id || "").slice(-6).toUpperCase()}</span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                    <div className="cb-badges">{getStatusLabel(booking)}</div>
                  </div>

                  <div className="cb-meta">
                    <div className="cb-meta-item">
                      <span className="cb-meta-ico" aria-hidden="true">
                        <CalendarDays size={15} />
                      </span>
                      <div>
                        <span className="cb-meta-label">When</span>
                        {(() => {
                          const tag = dayTagLabel(booking, today);
                          return tag ? (
                            <span className="cb-meta-value">{formatTimeRange12(booking.startTime, booking.endTime, "-")}</span>
                          ) : (
                            <span className="cb-meta-value">{formatDate(booking.date)} · {formatTimeRange12(booking.startTime, booking.endTime, "-")}</span>
                          );
                        })()}
                      </div>
                    </div>
                    {(booking.address || booking.addressDetails?.society || booking.location?.lat != null) && (
                      <div className="cb-meta-item cb-meta-where">
                        <div className="cb-where-body">
                          <span className="cb-meta-label">Customer location</span>
                          <span className="cb-meta-value cb-where-text">
                            <MapPin size={14} className="cb-where-icon" aria-hidden="true" />
                            {customerLocationLabel(booking) || "Customer location"}
                          </span>
                          {mapsNavigateUrl(booking) && (
                            <a
                              className="cook-addr-directions cb-directions"
                              href={mapsNavigateUrl(booking)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Open customer's location in Google Maps"
                            >
                              <Navigation size={15} />
                              {booking.location?.lat != null ? "Open GPS pin in Maps" : "Get Directions"}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {booking.notes && (
                    <p className="cb-note">
                      <span className="cb-note-label">Note</span> {booking.notes}
                    </p>
                  )}

                  {booking.hoursCompleted && (
                    <div className="cb-hours-done">
                      <BellRing size={16} />
                      <span>Cooking hours complete — please wrap up the session.</span>
                    </div>
                  )}

                  {booking.status === "requested" && (
                    <div className="booking-actions-row cb-actions cook-request-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={actingOn === booking._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestAction(booking, "accept");
                        }}
                        aria-label={`Accept booking from ${booking.customer?.name || "customer"}`}
                      >
                        <Check size={16} /> {actingOn === booking._id ? "Accepting…" : "Accept"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger-outline"
                        disabled={actingOn === booking._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestAction(booking, "reject");
                        }}
                        aria-label={`Ignore booking from ${booking.customer?.name || "customer"}`}
                      >
                        <XCircle size={16} /> {actingOn === booking._id ? "Ignoring…" : "Ignore"}
                      </button>
                    </div>
                  )}

                  {booking.status === "accepted" && !isPaidBooking(booking) && (
                    <div className="cb-hours-done" style={{ borderStyle: "dashed" }}>
                      <BellRing size={16} />
                      <span>Booking accepted — waiting for customer payment. It will appear in Today/Tomorrow once paid.</span>
                    </div>
                  )}

                  {["accepted", "confirmed", "in_progress"].includes(booking.status) && isPaidBooking(booking) && (
                    <div className="booking-actions-row cb-actions">
                      <Link to={`/bookings/${booking._id}`} className="btn btn-outline btn-sm">
                        {booking.serviceStartedAt ? "View more" : "Start with OTP"} <ArrowRight size={15} />
                      </Link>
                      {!booking.serviceStartedAt && !isCancelLocked(booking) && (
                        <button
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => setPendingCancelId(booking._id)}
                        >
                          <XCircle size={16} /> Cancel Booking
                        </button>
                      )}
                    </div>
                  )}
                  {booking.status === "accepted" && !isPaidBooking(booking) && !booking.serviceStartedAt && !isCancelLocked(booking) && (
                    <div className="booking-actions-row cb-actions">
                      <button
                        className="btn btn-danger-outline btn-sm"
                        onClick={() => setPendingCancelId(booking._id)}
                      >
                        <XCircle size={16} /> Cancel Booking
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            ) : (
              <div className="cook-empty">
                <div className="cook-empty-icon">
                  {view === "needs-action" ? (
                    <Inbox size={28} />
                  ) : view === "today" ? (
                    <CalendarCheck size={28} />
                  ) : view === "tomorrow" ? (
                    <CalendarClock size={28} />
                  ) : view === "upcoming" ? (
                    <CalendarDays size={28} />
                  ) : (
                    <History size={28} />
                  )}
                </div>
                <h3>
                  {view === "needs-action"
                    ? "No new requests"
                    : view === "today"
                    ? "No bookings today"
                    : view === "tomorrow"
                    ? "No bookings tomorrow"
                    : view === "upcoming"
                    ? "Nothing scheduled"
                    : "No past bookings"}
                </h3>
                <p>
                  {view === "needs-action"
                    ? "You're all caught up — new booking requests will pop up here."
                    : view === "today"
                    ? "Paid, confirmed bookings for today will show up here. Accepted requests appear once the customer pays."
                    : view === "tomorrow"
                    ? "Paid, confirmed bookings for tomorrow will show up here. Accepted requests appear once the customer pays."
                    : view === "upcoming"
                    ? "Accepted bookings will appear here with everything you need for the day."
                    : "Settled bookings (done or declined) will show up here."}
                </p>
              </div>
            )
          ) : (
            !loadingBookings && (
              <div className="cook-empty">
                <div className="cook-empty-icon">
                  <ChefHat size={28} />
                </div>
                <h3>No bookings yet</h3>
                <p>Stay marked Available so customers can find and book you.</p>
              </div>
            )
          )}
        </div>
      )}

      {/* EVENTS TAB: CookMitra-assigned event bookings (§13) with full
          event details + event earnings. */}
      {view === "events" && (
        <div>
          <div className="cev-page-head">
            <span className="cev-eyebrow">
              <ChefHat size={13} aria-hidden="true" /> Cook assigned
            </span>
            <h3>Event Assignments</h3>
            <p>Birthdays, anniversaries and family functions CookMitra assigns to you.</p>
          </div>
          <CookEventBookings />
        </div>
      )}

      {/* REPORTS TAB: complaints the cook filed — status + admin replies. */}
      {view === "reports" && (
        <div>
          <h3 style={{ fontSize: "1.1rem", marginBottom: "0.25rem" }}>My reports to support</h3>
          <p style={{ color: "var(--slate-500)", fontSize: "0.9rem", marginBottom: "1rem" }}>
            Issues you reported about customers. File a new one from the booking details page.
          </p>
          {!myComplaints || myComplaints.length === 0 ? (
            <div className="cook-empty">
              <div className="cook-empty-icon">
                <ShieldCheck size={28} />
              </div>
              <h3>No reports filed</h3>
              <p>If a customer causes trouble, report it from the booking and track it here.</p>
            </div>
          ) : (
            <div className="bookings-list-modern cook-bookings-grid">
              {(myComplaints || []).map((c) => (
                <div key={c._id} className="booking-item-card cook-booking-card cb-card">
                  <div className="cb-top">
                    <div className="cb-id">
                      <span className="cook-avatar cb-avatar" aria-hidden="true">
                        <ShieldCheck size={18} />
                      </span>
                      <div className="cb-id-text">
                        <div className="cb-name-row">
                          <h3 style={{ textTransform: "capitalize" }}>
                            {(c.category || "other").replace(/_/g, " ")}
                          </h3>
                        </div>
                        <p className="cb-sub">
                          {c.booking?.date ? (
                            <span className="cb-sub-item">
                              <CalendarDays size={12} />
                              {formatDate(c.booking.date)}
                            </span>
                          ) : null}
                          {c.customer?.name ? (
                            <span className="cb-sub-item">
                              <UserRound size={12} />
                              {c.customer.name}
                            </span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                    <div className="cb-badges">
                      <span
                        className={`badge ${
                          c.status === "resolved"
                            ? "badge-emerald"
                            : c.status === "rejected"
                              ? "badge-rose"
                              : c.status === "in_review"
                                ? "badge-blue"
                                : "badge-amber"
                        }`}
                        style={{ textTransform: "capitalize" }}
                      >
                        {(c.status || "open").replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                  <p className="cb-note">{c.message}</p>
                  {c.adminNote && (
                    <p className="cb-note">
                      <span className="cb-note-label">Support reply</span> {c.adminNote}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <BookingRequestModal
        open={!!requestModalBooking}
        onClose={() => {
          const closedId = requestModalBooking?._id;
          if (closedId) handledRequestIds.current.add(String(closedId));
          setRequestModalBooking(null);
          popNextPendingRequest(closedId);
        }}
        booking={requestModalBooking}
        onAction={() => {
          const actedId = requestModalBooking?._id;
          if (actedId) handledRequestIds.current.add(String(actedId));
          refetchAllRequests();
          popNextPendingRequest(actedId);
        }}
      />
      {!!pendingCancelId && (
        <CookCancelModal
          bookingId={pendingCancelId}
          onClose={() => setPendingCancelId(null)}
          onCancelled={handleCancel}
        />
      )}
    </div>
  );
};

export default CookDashboard;
